import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Drawer,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Notifications as NoticeIcon,
  Refresh as RefreshIcon,
  Preview as PreviewIcon,
  Campaign as BannerIcon,
  ViewAgenda as CardIcon,
  TextRotateVertical as TickerIcon,
  PushPin as PinIcon,
  EditNote as EditNoteIcon,
  Category as CategoryIcon,
  Schedule as ScheduleIcon,
  Link as LinkIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import {
  getAllNotices,
  createNotice,
  updateNotice,
  deleteNotice,
  toggleNoticeActive,
  getNextNoticeOrder,
} from '../../services/noticeService';
import type { Notice, NoticeInput, NoticePriority, NoticeDisplayType, NoticeCategory } from '../../types/notice.types';
import { Timestamp } from 'firebase/firestore';
import { priorityStyles, categoryStyles } from '../components/notice/noticePriorityStyles';
import TipTapEditor from '../components/common/TipTapEditor';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const INITIAL_FORM: NoticeInput = {
  title: '',
  body: '',
  imageUrl: '',
  linkUrl: '',
  linkLabel: '자세히 보기',
  priority: 'normal',
  displayType: 'card',
  category: '서비스 안내',
  isActive: true,
  isPinned: false,
  startAt: null,
  endAt: null,
  order: 1,
  createdBy: '',
};

export default function NoticeManagementPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);
  const [formData, setFormData] = useState<NoticeInput>(INITIAL_FORM);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noticeToDelete, setNoticeToDelete] = useState<Notice | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  const loadNotices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllNotices({ sortOrder: 'asc' });
      setNotices(data);
    } catch (err) {
      console.error('공지사항 로드 실패:', err);
      setError('공지사항 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotices();
  }, [loadNotices]);

  const handleAddNew = async () => {
    const nextOrder = await getNextNoticeOrder();
    setFormData({ ...INITIAL_FORM, order: nextOrder });
    setEditingNotice(null);
    setEditorOpen(true);
  };

  const handleEdit = (notice: Notice) => {
    setEditingNotice(notice);
    setFormData({
      title: notice.title,
      body: notice.body,
      imageUrl: notice.imageUrl || '',
      linkUrl: notice.linkUrl || '',
      linkLabel: notice.linkLabel || '자세히 보기',
      priority: notice.priority,
      displayType: notice.displayType,
      category: notice.category,
      isActive: notice.isActive,
      isPinned: notice.isPinned,
      startAt: notice.startAt,
      endAt: notice.endAt,
      order: notice.order,
    });
    setEditorOpen(true);
  };

  const handleDeleteClick = (notice: Notice) => {
    setNoticeToDelete(notice);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!noticeToDelete) return;
    try {
      setSaving(true);
      await deleteNotice(noticeToDelete.id);
      setSnackbar({ open: true, message: '공지사항이 삭제되었습니다.', severity: 'success' });
      loadNotices();
    } catch {
      setSnackbar({ open: true, message: '삭제에 실패했습니다.', severity: 'error' });
    } finally {
      setSaving(false);
      setDeleteDialogOpen(false);
      setNoticeToDelete(null);
    }
  };

  const handleToggleActive = async (notice: Notice) => {
    try {
      await toggleNoticeActive(notice.id, !notice.isActive);
      setSnackbar({
        open: true,
        message: notice.isActive ? '공지사항이 비활성화되었습니다.' : '공지사항이 활성화되었습니다.',
        severity: 'info',
      });
      loadNotices();
    } catch {
      setSnackbar({ open: true, message: '상태 변경에 실패했습니다.', severity: 'error' });
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      setSnackbar({ open: true, message: '제목을 입력해주세요.', severity: 'error' });
      return;
    }
    if (!formData.body.trim() || formData.body === '<p><br></p>') {
      setSnackbar({ open: true, message: '내용을 입력해주세요.', severity: 'error' });
      return;
    }

    try {
      setSaving(true);
      const saveData = { ...formData };

      if (editingNotice) {
        await updateNotice(editingNotice.id, saveData);
        setSnackbar({ open: true, message: '공지사항이 수정되었습니다.', severity: 'success' });
      } else {
        await createNotice(saveData);
        setSnackbar({ open: true, message: '공지사항이 등록되었습니다.', severity: 'success' });
      }

      setEditorOpen(false);
      loadNotices();
    } catch {
      setSnackbar({ open: true, message: '저장에 실패했습니다.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEditor = () => {
    setEditorOpen(false);
    setEditingNotice(null);
    setFormData(INITIAL_FORM);
  };

  /** 날짜 문자열 → Timestamp 변환 */
  const toTimestamp = (val: string): Timestamp | null => {
    if (!val) return null;
    return Timestamp.fromDate(new Date(val));
  };

  /** Timestamp → 날짜 문자열 변환 (datetime-local input용) */
  const fromTimestamp = (ts: Timestamp | null | undefined): string => {
    if (!ts) return '';
    try {
      const d = (ts as any).toDate ? (ts as any).toDate() : new Date(ts as any);
      const offset = d.getTimezoneOffset() * 60000;
      return new Date(d.getTime() - offset).toISOString().slice(0, 16);
    } catch {
      return '';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'order',
      headerName: '순서',
      width: 65,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value} size="small" sx={{ minWidth: 32, bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 700 }} />
      ),
    },
    {
      field: 'priority',
      headerName: '우선순위',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const style = priorityStyles[params.value as NoticePriority];
        return (
          <Chip label={style?.label} size="small" sx={{ bgcolor: style?.bgColor, color: style?.color, fontWeight: 700, fontSize: 11 }} />
        );
      },
    },
    {
      field: 'category',
      headerName: '카테고리',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const style = categoryStyles[params.value as NoticeCategory];
        return (
          <Chip label={params.value} size="small" sx={{ bgcolor: style?.bgColor, color: style?.color, fontWeight: 600, fontSize: 11 }} />
        );
      },
    },
    {
      field: 'title',
      headerName: '제목',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {params.row.isPinned && <PinIcon sx={{ fontSize: 14, color: '#f59e0b' }} />}
          <Typography sx={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'displayType',
      headerName: '표시',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const icons: Record<string, JSX.Element> = {
          banner: <BannerIcon sx={{ fontSize: 16 }} />,
          card: <CardIcon sx={{ fontSize: 16 }} />,
          ticker: <TickerIcon sx={{ fontSize: 16 }} />,
        };
        const labels: Record<string, string> = { banner: '배너', card: '카드', ticker: '티커' };
        return (
          <Tooltip title={labels[params.value]}>
            <Chip icon={icons[params.value]} label={labels[params.value]} size="small" variant="outlined" sx={{ fontSize: 11 }} />
          </Tooltip>
        );
      },
    },
    {
      field: 'isActive',
      headerName: '상태',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value ? '활성' : '비활성'} size="small" color={params.value ? 'success' : 'default'} sx={{ fontWeight: 600 }} />
      ),
    },
    {
      field: 'updated_at',
      headerName: '수정일',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const date = params.value instanceof Timestamp ? params.value.toDate() : new Date(params.value);
        return <Typography variant="caption" color="text.secondary">{date.toLocaleDateString('ko-KR')}</Typography>;
      },
    },
    {
      field: 'actions',
      headerName: '관리',
      width: 140,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const notice = params.row as Notice;
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={notice.isActive ? '비활성화' : '활성화'}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleActive(notice); }} sx={{ color: notice.isActive ? '#4caf50' : '#9e9e9e' }}>
                {notice.isActive ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="수정">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(notice); }} sx={{ color: '#1976d2' }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="삭제">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteClick(notice); }} sx={{ color: '#f44336' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // 섹션 헤더 컴포넌트
  const SectionHeader = ({ icon, title, number }: { icon: React.ReactNode; title: string; number: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box sx={{
        width: 28, height: 28, borderRadius: '8px',
        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: '0.65rem', fontWeight: 800,
        boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
      }}>
        {number}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
        {icon}
        <Typography fontWeight={700} sx={{ fontSize: '0.9rem', color: '#0f172a' }}>{title}</Typography>
      </Box>
    </Box>
  );

  const editorPanel = (
    <Box sx={{ width: isMobile ? '100%' : 640, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f8fafc' }}>
      {/* 헤더 - 다크 그라디언트 */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        px: 3, py: 2.5,
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""', position: 'absolute', top: 0, right: 0,
          width: '40%', height: '100%',
          background: 'radial-gradient(ellipse at 80% 50%, rgba(59,130,246,0.15), transparent 70%)',
          pointerEvents: 'none',
        },
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#60a5fa', letterSpacing: '0.08em', mb: 0.5 }}>
              {editingNotice ? 'EDIT NOTICE' : 'NEW NOTICE'}
            </Typography>
            <Typography fontWeight={800} sx={{ fontSize: '1.25rem', color: '#fff', letterSpacing: '-0.02em' }}>
              {editingNotice ? '공지사항 수정' : '공지사항 등록'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseEditor} sx={{
            color: '#94a3b8', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', color: '#fff' },
          }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* 폼 영역 */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>

        {/* 1. 콘텐츠 작성 */}
        <Box sx={{ mb: 3 }}>
          <SectionHeader icon={<EditNoteIcon sx={{ fontSize: 18, color: '#3b82f6' }} />} title="콘텐츠 작성" number="01" />

          <TextField
            fullWidth
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="공지사항 제목을 입력하세요 *"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff', fontWeight: 600, fontSize: '1rem',
                borderRadius: 2,
                '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: 2 },
              },
            }}
          />

          <Box sx={{ mb: 2 }}>
            <TipTapEditor
              value={formData.body}
              onChange={(val) => setFormData({ ...formData, body: val })}
              placeholder="공지사항 내용을 입력하세요..."
              minHeight={220}
            />
          </Box>

          {/* 부가 정보 - 접힌 스타일 */}
          <Box sx={{
            display: 'flex', gap: 1.5, p: 2, bgcolor: '#fff', borderRadius: 2,
            border: '1px solid #e5e7eb',
          }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <ImageIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b' }}>이미지 URL</Typography>
              </Box>
              <TextField
                fullWidth
                value={formData.imageUrl}
                onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                placeholder="https://..."
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc', fontSize: '0.82rem', borderRadius: 1.5 } }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <LinkIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b' }}>링크 URL</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  value={formData.linkUrl}
                  onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                  placeholder="https://..."
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc', fontSize: '0.82rem', borderRadius: 1.5 } }}
                />
                <TextField
                  value={formData.linkLabel}
                  onChange={(e) => setFormData({ ...formData, linkLabel: e.target.value })}
                  size="small"
                  sx={{ minWidth: 100, '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc', fontSize: '0.82rem', borderRadius: 1.5 } }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* 2. 분류 설정 */}
        <Box sx={{ mb: 3 }}>
          <SectionHeader icon={<CategoryIcon sx={{ fontSize: 18, color: '#3b82f6' }} />} title="분류 설정" number="02" />

          <Box sx={{ p: 2.5, bgcolor: '#fff', borderRadius: 2.5, border: '1px solid #e5e7eb' }}>
            {/* 우선순위 */}
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', mb: 1 }}>우선순위</Typography>
            <ToggleButtonGroup
              value={formData.priority}
              exclusive
              onChange={(_, v) => v && setFormData({ ...formData, priority: v as NoticePriority })}
              sx={{ mb: 2.5, display: 'flex', gap: 0.8 }}
              size="small"
            >
              {(['urgent', 'important', 'normal'] as NoticePriority[]).map((p) => (
                <ToggleButton
                  key={p}
                  value={p}
                  sx={{
                    flex: 1, textTransform: 'none', fontWeight: 700, fontSize: '0.78rem',
                    borderRadius: '10px !important', border: '1.5px solid #e2e8f0 !important',
                    py: 1,
                    '&.Mui-selected': {
                      bgcolor: priorityStyles[p].bgColor,
                      borderColor: `${priorityStyles[p].color} !important`,
                      color: priorityStyles[p].color,
                      boxShadow: `0 2px 8px ${priorityStyles[p].bgColor}`,
                    },
                  }}
                >
                  {priorityStyles[p].label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {/* 표시 방식 - 비주얼 선택 카드 */}
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', mb: 1.5 }}>표시 방식</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
              {([
                {
                  value: 'banner' as NoticeDisplayType,
                  label: '배너',
                  desc: '상단 전체 너비 슬라이드',
                  icon: <BannerIcon sx={{ fontSize: 18 }} />,
                  preview: (
                    <Box sx={{ width: '100%', height: 40, borderRadius: 0.8, overflow: 'hidden', bgcolor: '#f1f5f9', position: 'relative' }}>
                      {/* 미니 배너 일러스트 */}
                      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 14, bgcolor: '#e2e8f0', borderRadius: '3px 3px 0 0' }} />
                      <Box sx={{ position: 'absolute', top: 16, left: 4, right: 4, height: 20, bgcolor: '#3b82f6', borderRadius: 0.5, opacity: 0.85 }}>
                        <Box sx={{ position: 'absolute', left: 6, top: 4, width: 30, height: 3, bgcolor: '#fff', borderRadius: 1, opacity: 0.9 }} />
                        <Box sx={{ position: 'absolute', left: 6, top: 10, width: 18, height: 2, bgcolor: '#fff', borderRadius: 1, opacity: 0.5 }} />
                        <Box sx={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 0.3 }}>
                          {[0,1,2].map(i => <Box key={i} sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: i === 0 ? '#fff' : 'rgba(255,255,255,0.4)' }} />)}
                        </Box>
                      </Box>
                    </Box>
                  ),
                },
                {
                  value: 'card' as NoticeDisplayType,
                  label: '카드',
                  desc: '접을 수 있는 카드형',
                  icon: <CardIcon sx={{ fontSize: 18 }} />,
                  preview: (
                    <Box sx={{ width: '100%', height: 40, borderRadius: 0.8, overflow: 'hidden', bgcolor: '#f1f5f9', p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                      {/* 미니 카드 일러스트 */}
                      {[0, 1].map(i => (
                        <Box key={i} sx={{ flex: 1, bgcolor: '#fff', borderRadius: 0.5, border: '1px solid #e2e8f0', px: 0.8, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 3, height: '60%', borderRadius: 0.5, bgcolor: i === 0 ? '#ef4444' : '#3b82f6' }} />
                          <Box>
                            <Box sx={{ width: 28, height: 2.5, bgcolor: '#334155', borderRadius: 1, mb: 0.3 }} />
                            <Box sx={{ width: 18, height: 2, bgcolor: '#94a3b8', borderRadius: 1 }} />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ),
                },
                {
                  value: 'ticker' as NoticeDisplayType,
                  label: '티커',
                  desc: '흐르는 뉴스 텍스트',
                  icon: <TickerIcon sx={{ fontSize: 18 }} />,
                  preview: (
                    <Box sx={{ width: '100%', height: 40, borderRadius: 0.8, overflow: 'hidden', bgcolor: '#f1f5f9', display: 'flex', flexDirection: 'column' }}>
                      {/* 미니 티커 일러스트 */}
                      <Box sx={{ height: 14, bgcolor: '#e2e8f0' }} />
                      <Box sx={{ height: 12, bgcolor: '#0f172a', display: 'flex', alignItems: 'center', px: 0.8, gap: 0.5 }}>
                        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#f59e0b', flexShrink: 0 }} />
                        <Box sx={{ width: 8, height: 2.5, bgcolor: '#f59e0b', borderRadius: 1, flexShrink: 0 }} />
                        <Box sx={{ display: 'flex', gap: 0.8, ml: 0.5 }}>
                          <Box sx={{ width: 20, height: 2, bgcolor: '#64748b', borderRadius: 1 }} />
                          <Box sx={{ width: 16, height: 2, bgcolor: '#64748b', borderRadius: 1 }} />
                          <Box sx={{ width: 22, height: 2, bgcolor: '#64748b', borderRadius: 1 }} />
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1, bgcolor: '#f1f5f9' }} />
                    </Box>
                  ),
                },
              ]).map((item) => {
                const isSelected = formData.displayType === item.value;
                return (
                  <Box
                    key={item.value}
                    onClick={() => setFormData({ ...formData, displayType: item.value })}
                    sx={{
                      flex: 1,
                      cursor: 'pointer',
                      borderRadius: 2.5,
                      border: `2px solid ${isSelected ? '#3b82f6' : '#e5e7eb'}`,
                      bgcolor: isSelected ? '#eff6ff' : '#fff',
                      p: 1.5,
                      transition: 'all 0.2s ease',
                      boxShadow: isSelected ? '0 4px 12px rgba(59,130,246,0.15)' : 'none',
                      '&:hover': {
                        borderColor: isSelected ? '#3b82f6' : '#cbd5e1',
                        boxShadow: isSelected ? '0 4px 12px rgba(59,130,246,0.15)' : '0 2px 8px rgba(0,0,0,0.04)',
                      },
                    }}
                  >
                    {/* 미니 프리뷰 */}
                    <Box sx={{ mb: 1.2, border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden' }}>
                      {item.preview}
                    </Box>
                    {/* 라벨 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                      <Box sx={{ color: isSelected ? '#2563eb' : '#64748b' }}>{item.icon}</Box>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: isSelected ? '#1d4ed8' : '#334155' }}>
                        {item.label}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', lineHeight: 1.3 }}>
                      {item.desc}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* 카테고리 */}
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', mb: 1 }}>카테고리</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as NoticeCategory })}
                sx={{ bgcolor: '#f8fafc', borderRadius: 2, '& fieldset': { borderColor: '#e5e7eb' } }}
              >
                {(['서비스 안내', '시세 변동', '이벤트', '점검 안내'] as NoticeCategory[]).map((cat) => (
                  <MenuItem key={cat} value={cat}>{categoryStyles[cat].icon} {cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* 3. 게시 설정 */}
        <Box sx={{ mb: 3 }}>
          <SectionHeader icon={<ScheduleIcon sx={{ fontSize: 18, color: '#3b82f6' }} />} title="게시 설정" number="03" />

          <Box sx={{ p: 2.5, bgcolor: '#fff', borderRadius: 2.5, border: '1px solid #e5e7eb' }}>
            {/* 스위치 + 순서 - 가로 배치 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
                bgcolor: formData.isActive ? 'rgba(34,197,94,0.06)' : '#f8fafc',
                border: `1.5px solid ${formData.isActive ? 'rgba(34,197,94,0.3)' : '#e5e7eb'}`,
                borderRadius: 2, transition: 'all 0.2s',
              }}>
                <Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} color="success" size="small" />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: formData.isActive ? '#16a34a' : '#94a3b8' }}>활성화</Typography>
              </Box>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
                bgcolor: formData.isPinned ? 'rgba(245,158,11,0.06)' : '#f8fafc',
                border: `1.5px solid ${formData.isPinned ? 'rgba(245,158,11,0.3)' : '#e5e7eb'}`,
                borderRadius: 2, transition: 'all 0.2s',
              }}>
                <Switch checked={formData.isPinned} onChange={(e) => setFormData({ ...formData, isPinned: e.target.checked })} color="warning" size="small" />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: formData.isPinned ? '#d97706' : '#94a3b8' }}>상단 고정</Typography>
              </Box>
              <TextField
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
                size="small"
                InputProps={{ inputProps: { min: 1 }, startAdornment: <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mr: 0.5, whiteSpace: 'nowrap' }}>순서</Typography> }}
                sx={{ width: 90, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
              />
            </Box>

            {/* 기간 설정 */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                fullWidth
                type="datetime-local"
                label="시작일 (비워두면 즉시)"
                value={fromTimestamp(formData.startAt as any)}
                onChange={(e) => setFormData({ ...formData, startAt: toTimestamp(e.target.value) })}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
              />
              <TextField
                fullWidth
                type="datetime-local"
                label="종료일 (비워두면 무기한)"
                value={fromTimestamp(formData.endAt as any)}
                onChange={(e) => setFormData({ ...formData, endAt: toTimestamp(e.target.value) })}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f8fafc' } }}
              />
            </Box>
          </Box>
        </Box>

        {/* 4. 미리보기 */}
        <Box sx={{ mb: 1 }}>
          <SectionHeader icon={<PreviewIcon sx={{ fontSize: 18, color: '#3b82f6' }} />} title="미리보기" number="04" />

          <Box sx={{ bgcolor: '#fff', borderRadius: 2.5, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            {formData.title ? (
              <Box>
                <Box sx={{
                  px: 2.5, py: 2,
                  background: priorityStyles[formData.priority].gradient,
                  borderBottom: `2px solid ${priorityStyles[formData.priority].borderColor}`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip label={priorityStyles[formData.priority].label} size="small" sx={{ bgcolor: priorityStyles[formData.priority].color, color: '#fff', fontWeight: 700, height: 22, fontSize: '0.65rem' }} />
                    <Chip label={formData.category} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.65rem' }} />
                    <Chip label={formData.displayType === 'banner' ? '배너' : formData.displayType === 'card' ? '카드' : '티커'} size="small" sx={{ height: 22, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.2)', color: '#333' }} />
                  </Box>
                  <Typography fontWeight={700} sx={{ fontSize: '0.95rem', color: '#0f172a' }}>{formData.title}</Typography>
                </Box>
                {formData.body && formData.body !== '<p><br></p>' && (
                  <Box sx={{
                    px: 2.5, py: 2, fontSize: '0.82rem', color: '#475569', lineHeight: 1.7,
                    '& p': { m: 0, mb: 0.5 },
                    '& img': { maxWidth: '100%', borderRadius: 1, my: 1 },
                    '& h1, & h2, & h3': { mt: 1, mb: 0.5, color: '#0f172a' },
                    '& ul, & ol': { pl: 2.5 },
                    '& a': { color: '#2563eb' },
                  }} dangerouslySetInnerHTML={{ __html: formData.body }} />
                )}
                {formData.imageUrl && (
                  <Box sx={{ px: 2.5, pb: 2 }}>
                    <Box component="img" src={formData.imageUrl} alt="" sx={{ maxWidth: '100%', borderRadius: 2, border: '1px solid #e5e7eb' }} />
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <PreviewIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8' }}>
                  제목과 내용을 입력하면<br />미리보기가 표시됩니다
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* 하단 저장 바 */}
      <Box sx={{
        p: 2.5, bgcolor: '#fff',
        borderTop: '1px solid #e5e7eb',
        display: 'flex', gap: 1.5,
        boxShadow: '0 -4px 16px rgba(0,0,0,0.04)',
      }}>
        <Button
          variant="outlined"
          onClick={handleCloseEditor}
          fullWidth
          sx={{ py: 1.3, borderRadius: 2.5, fontWeight: 600, borderColor: '#d1d5db', color: '#64748b' }}
        >
          취소
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          fullWidth
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{
            py: 1.3, borderRadius: 2.5, fontWeight: 700,
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            boxShadow: '0 4px 14px rgba(59,130,246,0.35)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
              boxShadow: '0 6px 20px rgba(59,130,246,0.45)',
            },
          }}
        >
          {saving ? '저장 중...' : editingNotice ? '수정 완료' : '등록 완료'}
        </Button>
      </Box>
    </Box>
  );

  // 통계 데이터
  const activeCount = notices.filter((n) => n.isActive).length;
  const urgentCount = notices.filter((n) => n.priority === 'urgent' && n.isActive).length;
  const scheduledCount = notices.filter((n) => {
    if (!n.startAt) return false;
    const startTime = (n.startAt as any)?.toMillis ? (n.startAt as any).toMillis() : new Date(n.startAt as any).getTime();
    return startTime > Date.now();
  }).length;

  return (
    <Box sx={{ animation: `${fadeIn} 0.4s ease-out`, pb: 4 }}>
      {/* 페이지 헤더 */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', borderRadius: 3, color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <NoticeIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.5px' }}>공지사항 관리</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>공지사항을 등록하고 관리합니다</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="outlined" onClick={loadNotices} startIcon={<RefreshIcon />} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
              새로고침
            </Button>
            <Button variant="contained" onClick={handleAddNew} startIcon={<AddIcon />} sx={{ bgcolor: '#fff', color: '#1976d2', fontWeight: 700, '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}>
              공지 등록
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* 통계 카드 */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          { label: '전체 공지', value: notices.length, color: 'primary' },
          { label: '활성 공지', value: activeCount, color: 'success.main' },
          { label: '긴급 공지', value: urgentCount, color: '#dc2626' },
          { label: '예약 공지', value: scheduledCount, color: '#7c3aed' },
        ].map((stat) => (
          <Card key={stat.label} elevation={0} sx={{ flex: '1 1 150px', border: '1px solid #e0e7ef', borderRadius: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>{stat.label}</Typography>
              <Typography variant="h4" fontWeight={800} sx={{ color: stat.color }}>{stat.value}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* 공지 목록 */}
      <Paper elevation={0} sx={{ border: '1px solid #e0e7ef', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#f5f7fa', borderBottom: '1px solid #e0e7ef' }}>
          <Typography variant="subtitle2" fontWeight={700}>공지사항 목록</Typography>
        </Box>
        <DataGrid
          rows={notices}
          columns={columns}
          loading={loading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: { sortModel: [{ field: 'order', sort: 'asc' }] },
          }}
          onRowClick={(params: GridRowParams) => handleEdit(params.row as Notice)}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': { borderColor: '#f0f0f0', py: 1.5 },
            '& .MuiDataGrid-columnHeaders': { bgcolor: '#fafbfc', borderColor: '#e0e7ef' },
            '& .MuiDataGrid-row': { cursor: 'pointer', '&:hover': { bgcolor: '#f8f9ff' } },
            '& .MuiDataGrid-footerContainer': { borderColor: '#e0e7ef' },
          }}
          localeText={{
            noRowsLabel: '공지사항이 없습니다',
            MuiTablePagination: {
              labelRowsPerPage: '표시 개수:',
              labelDisplayedRows: ({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `${to}+`}`,
            },
          }}
        />
      </Paper>

      {/* 편집 드로어 */}
      <Drawer anchor="right" open={editorOpen} onClose={handleCloseEditor} PaperProps={{ sx: { width: isMobile ? '100%' : 640, boxShadow: '-12px 0 40px rgba(0,0,0,0.12)' } }}>
        {editorPanel}
      </Drawer>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 2, maxWidth: 400 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            <Typography fontWeight={700}>공지사항 삭제</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            이 공지사항을 삭제하시겠습니까?
            <Box component="span" sx={{ display: 'block', mt: 1, fontWeight: 600, color: 'text.primary' }}>
              "{noticeToDelete?.title}"
            </Box>
            <Box component="span" sx={{ display: 'block', mt: 1, color: 'error.main', fontSize: 13 }}>
              삭제된 데이터는 복구할 수 없습니다.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">취소</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" disabled={saving} startIcon={saving && <CircularProgress size={16} color="inherit" />}>
            {saving ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ minWidth: 280 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
